/* --------------------------------------------------------------------------- */
/*  getTranslation.ts                                                          
/*  ------------------------------------------------------------------------- */
/*  Very small runtime helper – converts a fully-qualified i18next key of the  */
/*  form  "<namespace>.<rest.of.key>"  into an i18next lookup *in that same    */
/*  namespace*, falling back to the supplied defaultValue when the key is      */
/*  missing.                                                                   */
/*                                                                             */
/*  ⚠️  DESIGN GOAL                                                             */
/*  ------------------------------------------------------------------------- */
/*  This util MUST NOT import the global `i18n` instance directly.             */
/*  – Keeps the `utils/spheres` package free of cross-package compile issues.  */
/*  – Lets any consumer (React, Node, tests, etc.) decide which configured     */
/*    i18next instance to pass in.                                             */
/* --------------------------------------------------------------------------- */

export type I18nLike = {
  /**
   * Minimal surface of an i18next instance that we actually need.
   * (A real i18next object has many more methods.)
   */
  t: (key: string, options?: any) => string;
};

/**
 * Safe one-liner for translations that come to us *already* prefixed
 * with a namespace (e.g. `"schema.center_intention.name"`).
 *
 * @param i18n       – *The* i18next instance that YOUR APP has already
 *                     initialised (with language, bundles, etc.)
 * @param fullKey    – Fully-qualified key *including* namespace.
 *                     If it does **not** contain a dot, we assume it’s
 *                     already in the default namespace.
 * @param fallback   – Value to return when the key is missing.
 *                     Defaults to the empty string.
 *
 * @returns Localised string if found, otherwise `fallback`.
 *
 * @example
 * ```ts
 * import { i18n }           from 'localization';
 * import { getTranslation } from 'spheres/lib/localization/getTranslation';
 *
 * const label = getTranslation(
 *   i18n,
 *   'schema.center_intention.name',
 *   'Intention'           // fallback
 * );
 * ```
 */
export function getTranslation(
  i18n: I18nLike,
  fullKey: string | undefined | null,
  fallback = ''
): string {
  if (!fullKey) return fallback;

  /* -----------------------------------------------------------------------
     Split the key on the FIRST dot:
       "schema.center_intention.name"
        └─namespace─┘ └─ key → center_intention.name
     --------------------------------------------------------------------- */
  const firstDot = fullKey.indexOf('.');

  //
  /* -----------------------------------------------------------------------
     No namespace?  Use whatever i18next’s default namespace is.
       nsSeparator: false required to support system names with colons
     --------------------------------------------------------------------- */
  if (firstDot === -1) {
    return i18n.t(fullKey, { nsSeparator: false, defaultValue: fallback });
  }

  const namespace = fullKey.slice(0, firstDot); // "schema"
  const innerKey = fullKey.slice(firstDot + 1); // "center_intention.name"

  return i18n.t(innerKey, { ns: namespace, defaultValue: fallback });
}
